import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import { Image } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { Player } from '@sportninja/common/types/Player'
import { status } from '@sportninja/common/constants/attendanceStatus'
import req from '@sportninja/common/api/request'

interface ManageTeamAttendanceModalContentProps {
  players?: Player[]
  gameId?: string
  rosterId?: string
  refetch?: () => void
  readAttendanceInformation: () => void
}

const Container = styled.div`
  background-color: ${colors.SECONDARY_300};
  color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  max-height: 500px;
  min-height: 500px;
  overflow-y: auto;
`

const Alert = styled.div`
  font-size: 14px;
  line-height: 20px;
  background-color: #360000;
  border: 1px solid ${colors.ERROR_200};
  color: ${colors.ERROR_100};
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`

const Table = styled.table`
  width: 100%;
`

const TableHead = styled.thead`
  text-align: left;
  background-color: ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_50};
  font-size: 14px;
  line-height: 20px;
  /* These styles are not being applied to the table header cells */
  /* We need to target the th elements directly */
  & th {
    padding: 8px;
    /* only th */
    &:first-child {
      border-top-left-radius: 8px;
    }
    &:last-child {
      border-top-right-radius: 8px;
    }
  }
`

const TableRow = styled.tr<{ isDuplicate?: boolean }>`
  color: ${(props) => (props.isDuplicate ? colors.ERROR_200 : 'inherit')};
  &:nth-child(even) {
    background-color: ${colors.SECONDARY_200};
  }
`

const TableCell = styled.td<{
  isDuplicate?: boolean
  clicable?: boolean
}>`
  padding: 8px;
  overflow: hidden;
  color: ${(props) =>
    props.isDuplicate
      ? colors.ERROR_200
      : props.clicable
      ? colors.PRIMARY
      : colors.NEUTRAL_0};
  text-overflow: ellipsis;
  font-size: 16px;
  font-weight: 400;
  line-height: 23.2px; /* 145% */
  text-align: center;
  vertical-align: middle;
  cursor: ${(props) => (props.clicable ? 'pointer' : 'default')};
`

const PlayerImageWrapper = styled.div`
  width: 32px;
  height: 32px;
  margin-right: 8px;
  overflow: hidden;
  border-radius: 50%;
  background-color: ${colors.NEUTRAL_0};
`

const StatusContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
`

const StyledIcon = styled.span`
  font-size: 20px;
  cursor: pointer;
`

const StyledInput = styled.input`
  width: 60px;
  text-align: center;
  display: flex;
  padding: 8px;
  font-size: 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_0};
  font-size: 16px;
  font-weight: 400;
  line-height: 145%; /* 23.2px */
  -moz-appearance: textfield; /* Firefox */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

const AffiliateTag = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 145%; /* 17.4px */
  color: ${colors.NEUTRAL_50};
`

const ManageTeamAttendanceModalContent: React.FC<
  ManageTeamAttendanceModalContentProps
> = ({
  players = [],
  gameId,
  rosterId,
  refetch,
  readAttendanceInformation,
}) => {
  const getDuplicateJerseyNumbers = () => {
    const jerseyNumbers = players.map((player) => player?.player_number)
    return jerseyNumbers.filter(
      (number, index) => jerseyNumbers.indexOf(number) !== index
    )
  }

  const duplicateJerseyNumbers = getDuplicateJerseyNumbers()
  const [isEditing, setIsEditing] = useState<string | null>(null)
  const [editedNumber, setEditedNumber] = useState<string>('')

  const updatePlayerAttendance = useCallback(
    async (
      playerId: string,
      playerNumber: number,
      attendanceStatus: number
    ) => {
      try {
        await req(
          `/games/${gameId}/rosters/${rosterId}/players/${playerId}/attendance-data`,
          {
            method: 'PUT',
            body: JSON.stringify({
              player_number: playerNumber,
              attendance_status: attendanceStatus,
            }),
          }
        )
        refetch?.()
        readAttendanceInformation?.()
      } catch (e) {
        console.error(e)
      }
    },
    [gameId, rosterId, refetch, readAttendanceInformation]
  )

  const updateAttendanceStatus = useCallback(
    async (playerId: string, attendanceStatus: number | null) => {
      try {
        await req(
          `/games/${gameId}/rosters/${rosterId}/players/${playerId}/attendance`,
          {
            method: 'PUT',
            body: JSON.stringify({ attendance_status: attendanceStatus }),
          }
        )
        refetch?.()
        readAttendanceInformation?.()
      } catch (e) {
        console.error(e)
      }
    },
    [gameId, rosterId, refetch, readAttendanceInformation]
  )

  if (players.length === 0) {
    return <Container>No players available.</Container>
  }

  return (
    <Container>
      {duplicateJerseyNumbers.length > 0 && (
        <Alert>
          <ExclamationCircleOutlined
            style={{
              marginRight: '0.5rem',
              fontSize: 16,
              color: colors.ERROR_100,
            }}
          />
          You have {duplicateJerseyNumbers.length} players with the same jersey
          number
        </Alert>
      )}

      <Table>
        <TableHead>
          <tr>
            <th>#</th>
            <th>Player Name</th>
            <td style={{ textAlign: 'center', paddingLeft: 8 }}>Position</td>
            <th style={{ textAlign: 'center', paddingLeft: 8 }}>Status</th>
          </tr>
        </TableHead>
        <tbody>
          {players
            .sort((a, b) => {
              return a.is_affiliate === b.is_affiliate
                ? 0
                : a.is_affiliate
                ? 1
                : -1
            })
            .map((player) => (
              <TableRow
                key={player.id}
                isDuplicate={duplicateJerseyNumbers.includes(
                  player?.player_number
                )}
              >
                <TableCell style={{ textAlign: 'center', borderWidth: 1 }}>
                  {isEditing === player.id ? (
                    <StyledInput
                      maxLength={3}
                      type='number'
                      value={editedNumber}
                      onChange={(e) => setEditedNumber(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          updatePlayerAttendance(
                            player.id,
                            Number(editedNumber),
                            player.attendance_status
                          )
                          setIsEditing(null)
                        }
                      }}
                      onBlur={() => setIsEditing(null)}
                      autoFocus
                    />
                  ) : (
                    <TableCell
                      clicable={true}
                      isDuplicate={duplicateJerseyNumbers.includes(
                        player?.player_number
                      )}
                      onClick={() => {
                        setIsEditing(player.id)
                        setEditedNumber(player.player_number?.toString() || '')
                      }}
                    >
                      {player?.player_number}
                    </TableCell>
                  )}
                </TableCell>
                <TableCell
                // isDuplicate={duplicateJerseyNumbers.includes(
                //   player?.player_number
                // )}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <PlayerImageWrapper>
                      <Image
                        src={player.image}
                        alt={player?.full_name}
                        preview={false}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        fallback={
                          '/images/generic_placeholders/placeholder_player.png'
                        }
                      />
                    </PlayerImageWrapper>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                      }}
                    >
                      {player?.name_first} {player?.name_last}
                      {player?.is_affiliate && (
                        <AffiliateTag>Affiliate</AffiliateTag>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell
                  style={{
                    textAlign: 'center',
                    borderWidth: 1,
                  }}
                >
                  {player?.player_type?.abbreviation}
                </TableCell>
                <TableCell
                  style={{
                    alignSelf: 'center',
                    borderWidth: 1,
                  }}
                >
                  <StatusContainer>
                    <StyledIcon
                      as={CheckCircleOutlined}
                      style={{
                        color:
                          player?.attendance_status === status.ATTENDING
                            ? colors.SUCCESS_200
                            : colors.NEUTRAL_50,
                      }}
                      onClick={async () => {
                        if (player?.attendance_status === status.ATTENDING) {
                          await updateAttendanceStatus(player.id, null)
                        } else {
                          await updateAttendanceStatus(
                            player.id,
                            status.ATTENDING
                          )
                        }
                      }}
                    />
                    <StyledIcon
                      as={QuestionCircleOutlined}
                      style={{
                        color:
                          player?.attendance_status === status.MAYBE
                            ? colors.WARNING_200
                            : colors.NEUTRAL_50,
                      }}
                      onClick={async () => {
                        if (player?.attendance_status === status.MAYBE) {
                          await updateAttendanceStatus(player.id, null)
                        } else {
                          await updateAttendanceStatus(player.id, status.MAYBE)
                        }
                      }}
                    />
                    <StyledIcon
                      as={CloseCircleOutlined}
                      style={{
                        color:
                          player?.attendance_status === status.NOT_ATTENDING
                            ? colors.ERROR_200
                            : colors.NEUTRAL_50,
                      }}
                      onClick={async () => {
                        if (
                          player?.attendance_status === status.NOT_ATTENDING
                        ) {
                          await updateAttendanceStatus(player.id, null)
                        } else {
                          await updateAttendanceStatus(
                            player.id,
                            status.NOT_ATTENDING
                          )
                        }
                      }}
                    />
                  </StatusContainer>
                </TableCell>
              </TableRow>
            ))}
        </tbody>
      </Table>
    </Container>
  )
}

export default ManageTeamAttendanceModalContent
